/**
 * Canonical JSON + SHA-256 utilities for immutable lineage digests.
 * Web Crypto only — works in browsers, CF Workers, Deno, Node 18+.
 */

import { Data, Effect } from 'effect';

export class ParserDigestError extends Data.TaggedError('ParserDigestError')<{
  readonly operation: 'canonical-json' | 'sha256';
  readonly message: string;
}> {}

const parserDigestErrorCauses = new WeakMap<ParserDigestError, unknown>();

function parserDigestFailure(
  operation: ParserDigestError['operation'],
  message: string,
  cause: unknown,
): ParserDigestError {
  const error = new ParserDigestError({ operation, message });
  parserDigestErrorCauses.set(error, cause);
  return error;
}

export function parserDigestErrorCause(error: ParserDigestError): unknown {
  return parserDigestErrorCauses.get(error);
}

function canonicalize(value: unknown): unknown {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((item) => canonicalize(item));

  const obj = value as Record<string, unknown>;
  const sorted: Record<string, unknown> = {};
  for (const key of Object.keys(obj).sort()) {
    sorted[key] = canonicalize(obj[key]);
  }
  return sorted;
}

export function canonicalJson(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

export const sha256HexEffect = Effect.fn('sha256HexEffect')((input: string) =>
  Effect.tryPromise({
    try: async () => {
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
      return Array.from(new Uint8Array(digest))
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
    },
    catch: (cause) => parserDigestFailure('sha256', 'failed to compute SHA-256 digest', cause),
  }),
);

export function sha256Hex(input: string): Promise<string> {
  return Effect.runPromise(sha256HexEffect(input));
}

export const digestObjectEffect = Effect.fn('digestObjectEffect')(function* (value: unknown) {
  const json = yield* Effect.try({
    try: () => canonicalJson(value),
    catch: (cause) =>
      parserDigestFailure('canonical-json', 'failed to canonicalize value for digest', cause),
  });
  return yield* sha256HexEffect(json);
});

export function digestObject(value: unknown): Promise<string> {
  return Effect.runPromise(digestObjectEffect(value));
}
